/**
 * Browser push notifications via Firebase Cloud Messaging — TRUS-668.
 *
 * The backend sends a push to every active `push_subscriptions` row of a user
 * (message, call, notification). This module puts THIS browser in that list
 * with an FCM registration token:
 *
 *   GET /api/notifications/web-push/config  -> {enabled, firebase, vapid_key}
 *   firebase/messaging isSupported() + getToken(vapidKey, OUR service worker)
 *   POST /api/notifications/web-push/subscribe {fcm_token, device_type: 'web'}
 *
 *  - `syncWebPush` runs after sign-in / on each app load. If push is enabled on
 *    the server AND the user already granted permission, it gets the token
 *    again (Firebase refreshes it) and registers it, retiring the previous one
 *    when it changed. It never asks for permission: a prompt on page load is
 *    blocked by browsers and annoys people.
 *  - `enableWebPush` is for a click ("Enable desktop notifications"): it asks
 *    for permission, then gets a token and registers it.
 *  - `disableWebPush` runs on sign-out (and when the user switches it off): it
 *    tells the server, then deletes the token (`deleteToken`, which also drops
 *    the browser's push subscription).
 *
 * The Firebase SDK is loaded lazily (dynamic import) only on those paths, so the
 * main bundle does not grow. Its config comes from the backend: no build-time env
 * vars, and dev/prod follow the API they talk to.
 *
 * Skipped inside the desktop app (Tauri shows native notifications) and in any
 * secondary window (pop-out, iframe tab): only the top-level main window
 * registers.
 *
 * The service worker that shows the notifications is public/service-worker.js
 * (no Firebase SW SDK: it reads the raw FCM data message).
 *
 * Failures (TRUS-668): every step that can fail is named (config, permission,
 * service_worker, get_token, register). A failure is classified for the user
 * (blocked / unsupported / service setup / unknown: `WebPushFailure`), kept as
 * the last failure, broadcast with status 'error' so a banner or settings card
 * can say why, and reported once per page to
 * POST /api/notifications/web-push/client-error so the real Firebase error
 * (API key restriction, disabled API, VAPID mismatch...) shows in the backend
 * logs. Nothing is retried automatically.
 */
import { isSecondaryAppWindow } from '@/lib/desktopWindow';

export const SERVICE_WORKER_URL = '/service-worker.js';

/** Public Firebase web-app config (served by the backend; not a secret). */
export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  messagingSenderId: string;
  appId: string;
  storageBucket?: string;
  measurementId?: string;
}

export interface WebPushConfig {
  enabled: boolean;
  provider?: 'fcm';
  firebase: FirebaseWebConfig | null;
  vapid_key: string | null;
}

export interface WebPushRegistrationBody {
  fcm_token: string;
  device_type: 'web';
  browser?: string;
  os?: string;
}

/** The step that failed (the backend's `stage`). */
export type WebPushStage = 'config' | 'permission' | 'service_worker' | 'get_token' | 'register';

/**
 * What the user is told:
 *  - blocked: notifications are blocked for this site;
 *  - unsupported: this browser cannot receive push;
 *  - service: the notification service refused (a setup problem on our side);
 *  - network: this browser could not reach Google's push servers at all
 *    (firebaseinstallations / fcmregistrations.googleapis.com) - usually a
 *    firewall, proxy or filter on the user's network (TRUS-668);
 *  - unknown: anything else (try again).
 */
export type WebPushFailureKind = 'blocked' | 'unsupported' | 'service' | 'network' | 'unknown';

export interface WebPushFailure {
  stage: WebPushStage;
  /** Firebase error code (`messaging/token-subscribe-failed`), DOMException name, or `http-<status>`. */
  code: string;
  message: string;
  kind: WebPushFailureKind;
}

export interface WebPushClientErrorBody {
  stage: WebPushStage;
  code: string;
  message: string;
  user_agent_family: string;
}

/** How this module talks to the backend (staff and member portals differ). */
export interface WebPushTransport {
  getConfig(): Promise<WebPushConfig>;
  register(body: WebPushRegistrationBody): Promise<void>;
  unregister(token: string): Promise<void>;
  /** Best-effort report of a failed step (never throws to the caller). */
  reportError?(body: WebPushClientErrorBody): Promise<void>;
}

export type WebPushStatus =
  | 'unsupported' // no service worker / Push API, desktop app, or a secondary window
  | 'disabled' // push is not set up on the server
  | 'denied' // the user blocked notifications for this site
  | 'default' // not asked yet: show the "Enable" control
  | 'subscribed'
  | 'error';

// --------------------------------------------------------------------------- //
// environment
// --------------------------------------------------------------------------- //
export function isTauriApp(): boolean {
  return typeof window !== 'undefined' && '__TAURI__' in window;
}

/** Browser supports Web Push and this is a window that should register. */
export function isWebPushSupported(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (isTauriApp()) return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (!('Notification' in window)) return false;
  if (isSecondaryAppWindow()) return false;
  return true;
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

// --------------------------------------------------------------------------- //
// helpers
// --------------------------------------------------------------------------- //
/** base64url (Web Push certificate public key) -> bytes, to compare applicationServerKey. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function bytesEqual(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return false;
  const view = new Uint8Array(a);
  if (view.length !== b.length) return false;
  for (let i = 0; i < view.length; i += 1) if (view[i] !== b[i]) return false;
  return true;
}

function describeDevice(): { browser?: string; os?: string } {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : undefined;
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Android/.test(ua)
      ? 'Android'
      : /iPhone|iPad|iPod/.test(ua)
        ? 'iOS'
        : /Mac OS X/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : undefined;
  return { browser, os };
}

/** "Chrome 130 / macOS": browser family, major version and OS. No full user agent. */
export function userAgentFamily(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const { browser, os } = describeDevice();
  const versionPattern: Record<string, RegExp> = {
    Edge: /Edg\/(\d+)/,
    Opera: /OPR\/(\d+)/,
    Firefox: /Firefox\/(\d+)/,
    Chrome: /Chrome\/(\d+)/,
    Safari: /Version\/(\d+)/,
  };
  const version = browser ? versionPattern[browser]?.exec(ua)?.[1] : undefined;
  const name = browser ? `${browser}${version ? ` ${version}` : ''}` : 'Unknown browser';
  return `${name} / ${os ?? 'Unknown OS'}`;
}

// --------------------------------------------------------------------------- //
// failures: which step, what to tell the user, report once per page
// --------------------------------------------------------------------------- //
/** A step failed; carries the step name with the original error. */
export class WebPushStepError extends Error {
  readonly stage: WebPushStage;
  readonly original: unknown;

  constructor(stage: WebPushStage, original: unknown) {
    super(original instanceof Error ? original.message : String(original ?? 'failed'));
    this.name = 'WebPushStepError';
    this.stage = stage;
    this.original = original;
  }
}

async function step<T>(stage: WebPushStage, run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    throw err instanceof WebPushStepError ? err : new WebPushStepError(stage, err);
  }
}

function stepFailure(stage: WebPushStage, code: string, message: string): WebPushStepError {
  return new WebPushStepError(stage, Object.assign(new Error(message), { code }));
}

const BLOCKED_CODES = new Set(['messaging/permission-blocked', 'messaging/permission-default', 'denied']);
const UNSUPPORTED_CODES = new Set(['messaging/unsupported-browser', 'unsupported-browser']);

function errorCode(err: unknown): string {
  if (!err || typeof err !== 'object') return typeof err === 'string' ? err.slice(0, 100) : 'unknown';
  const e = err as { code?: unknown; name?: unknown; status?: unknown; response?: { status?: unknown } };
  const status = e.response?.status ?? e.status;
  if (typeof status === 'number') return `http-${status}`;
  if (typeof e.code === 'string' && e.code) return e.code;
  if (typeof e.name === 'string' && e.name && e.name !== 'Error') return e.name;
  return 'Error';
}

// fetch() rejects with a TypeError whose text names no server when the request
// never got an answer: Chrome "Failed to fetch", Firefox "NetworkError when
// attempting to fetch resource.", Safari "Load failed". Firebase's own codes for
// the same thing: installations/app-offline, messaging/... "network-error".
const NETWORK_MESSAGE = /failed to fetch|networkerror|network error|load failed|network request failed/i;
const NETWORK_CODES = new Set(['installations/app-offline', 'messaging/network-error', 'app/network-error']);

function isNetworkFailure(code: string, name: string, message: string): boolean {
  if (NETWORK_CODES.has(code)) return true;
  return (name === 'TypeError' || code === 'TypeError') && NETWORK_MESSAGE.test(message);
}

/** Which step failed, its code and message, and what the user is told. */
export function classifyWebPushError(err: unknown, fallbackStage: WebPushStage = 'get_token'): WebPushFailure {
  const stage = err instanceof WebPushStepError ? err.stage : fallbackStage;
  const original = err instanceof WebPushStepError ? err.original : err;
  const code = errorCode(original).slice(0, 100);
  const name = original && typeof original === 'object' ? String((original as { name?: unknown }).name ?? '') : '';
  const message = (
    original instanceof Error ? original.message : typeof original === 'string' ? original : ''
  ).slice(0, 300);

  let kind: WebPushFailureKind;
  if (BLOCKED_CODES.has(code) || name === 'NotAllowedError') kind = 'blocked';
  else if (UNSUPPORTED_CODES.has(code) || name === 'NotSupportedError') kind = 'unsupported';
  else if (stage === 'service_worker' && name === 'SecurityError') kind = 'unsupported';
  else if (stage === 'permission') kind = 'unknown';
  else if (isNetworkFailure(code, name, message)) kind = 'network';
  else kind = 'service';
  return { stage, code, message, kind };
}

let lastFailure: WebPushFailure | null = null;
const reported = new Set<string>();

/** The last failure on this page (cleared by a success). */
export function getLastWebPushFailure(): WebPushFailure | null {
  return lastFailure;
}

/** Tests / sign-out: forget the failure and what was reported. */
export function resetWebPushFailures(): void {
  lastFailure = null;
  reported.clear();
}

/** Remember it, report it to the backend once per page (stage + code), never throw. */
function recordFailure(transport: WebPushTransport, failure: WebPushFailure): WebPushFailure {
  lastFailure = failure;
  const key = `${failure.stage}:${failure.code}`;
  if (!reported.has(key) && transport.reportError) {
    reported.add(key);
    try {
      void transport
        .reportError({
          stage: failure.stage,
          code: failure.code,
          message: failure.message,
          user_agent_family: userAgentFamily(),
        })
        .catch(() => {});
    } catch {
      /* a report must never break the caller */
    }
  }
  return failure;
}

// --------------------------------------------------------------------------- //
// the FCM token this browser registered (to retire it when it changes or on
// sign-out; Firebase also keeps it in IndexedDB, but that is not ours to read)
// --------------------------------------------------------------------------- //
const TOKEN_KEY = 'weavein:fcm-web-token';

export function storedWebPushToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage blocked: the server copy is retired by FCM's UNREGISTERED */
  }
}

function isUsableConfig(config: WebPushConfig | null | undefined): config is WebPushConfig & {
  firebase: FirebaseWebConfig;
  vapid_key: string;
} {
  return !!(config && config.enabled && config.firebase && config.vapid_key);
}

// --------------------------------------------------------------------------- //
// Firebase (lazy)
// --------------------------------------------------------------------------- //
const FIREBASE_APP_NAME = 'weavein-web-push';

type MessagingModule = typeof import('firebase/messaging');
interface LoadedMessaging {
  mod: MessagingModule;
  messaging: import('firebase/messaging').Messaging;
}

/** Load firebase/app + firebase/messaging on demand; null when this browser cannot do FCM. */
async function loadMessaging(firebaseConfig: FirebaseWebConfig): Promise<LoadedMessaging | null> {
  const [appMod, mod] = await Promise.all([import('firebase/app'), import('firebase/messaging')]);
  if (!(await mod.isSupported())) return null;
  const existing = appMod.getApps().find((a) => a.name === FIREBASE_APP_NAME);
  const app = existing ?? appMod.initializeApp(firebaseConfig, FIREBASE_APP_NAME);
  return { mod, messaging: mod.getMessaging(app) };
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing) return existing;
  return navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' });
}

/** How long to wait for the service worker to become active. */
export const SERVICE_WORKER_READY_TIMEOUT_MS = 15_000;

async function activeRegistration(): Promise<ServiceWorkerRegistration> {
  await getRegistration();
  // getToken subscribes through pushManager, which needs an ACTIVE worker.
  // `ready` never settles when the worker cannot activate: bound it, so the
  // control does not spin forever and the failure is reported.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(Object.assign(new Error('service worker did not become active'), { code: 'sw-not-active' })),
      SERVICE_WORKER_READY_TIMEOUT_MS,
    );
  });
  try {
    return await Promise.race([navigator.serviceWorker.ready, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * A push subscription made with another key (the old self-signed VAPID key)
 * makes getToken fail: drop it first. Browsers that do not expose the key keep
 * their subscription.
 */
async function dropForeignSubscription(reg: ServiceWorkerRegistration, vapidKey: string): Promise<void> {
  const sub = await reg.pushManager.getSubscription();
  const current = sub?.options?.applicationServerKey;
  if (sub && current && !bytesEqual(current, urlBase64ToUint8Array(vapidKey))) {
    await sub.unsubscribe().catch(() => false);
  }
}

/** Get this browser's FCM token and register it (retiring a previous one). */
async function getTokenAndRegister(
  transport: WebPushTransport,
  config: WebPushConfig & { firebase: FirebaseWebConfig; vapid_key: string },
): Promise<WebPushStatus> {
  const reg = await step('service_worker', () => activeRegistration());
  await step('service_worker', () => dropForeignSubscription(reg, config.vapid_key));
  const loaded = await step('get_token', () => loadMessaging(config.firebase));
  if (!loaded) {
    throw stepFailure('get_token', 'messaging/unsupported-browser', 'firebase/messaging isSupported() is false');
  }
  const token = await step('get_token', () =>
    loaded.mod.getToken(loaded.messaging, {
      vapidKey: config.vapid_key,
      serviceWorkerRegistration: reg,
    }),
  );
  if (!token) throw stepFailure('get_token', 'no-token', 'FCM returned no token');
  const previous = storedWebPushToken();
  if (previous && previous !== token) {
    await transport.unregister(previous).catch((err) => console.warn('[webPush] retire old token failed', err));
  }
  // Always (re)register: idempotent on the server, and it moves the token to
  // whoever is signed in now and re-activates a row the server retired.
  await step('register', () => transport.register({ fcm_token: token, device_type: 'web', ...describeDevice() }));
  storeToken(token);
  lastFailure = null;
  return 'subscribed';
}

// --------------------------------------------------------------------------- //
// the last config each transport fetched (so a click can prompt before any
// await) and a status broadcast (so a banner hides once a silent sync or a
// settings toggle registered this browser)
// --------------------------------------------------------------------------- //
const knownConfig = new WeakMap<WebPushTransport, WebPushConfig>();

async function fetchConfig(transport: WebPushTransport): Promise<WebPushConfig> {
  const config = await transport.getConfig();
  if (config) knownConfig.set(transport, config);
  return config;
}

/** `failure` is set when status is 'error' (or 'denied' from a click). */
type StatusListener = (status: WebPushStatus, failure?: WebPushFailure | null) => void;
const statusListeners = new Set<StatusListener>();

/** Hear about status changes made anywhere on this page (sync, enable, disable). */
export function onWebPushStatusChange(listener: StatusListener): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

function emitStatus(status: WebPushStatus, failure: WebPushFailure | null = null): WebPushStatus {
  statusListeners.forEach((l) => {
    try {
      l(status, failure);
    } catch {
      /* a listener's problem */
    }
  });
  return status;
}

// --------------------------------------------------------------------------- //
// public API
// --------------------------------------------------------------------------- //
/**
 * After sign-in / on app load: refresh and register the token when permission
 * was already granted. Never prompts. Returns the resulting status (for UI).
 */
export async function syncWebPush(transport: WebPushTransport): Promise<WebPushStatus> {
  if (!isWebPushSupported()) return 'unsupported';
  try {
    const config = await step('config', () => fetchConfig(transport));
    if (!isUsableConfig(config)) return 'disabled';
    const permission = notificationPermission();
    if (permission === 'denied') return 'denied';
    if (permission !== 'granted') return 'default';
    return emitStatus(await getTokenAndRegister(transport, config));
  } catch (err) {
    const failure = recordFailure(transport, classifyWebPushError(err, 'config'));
    console.warn('[webPush] sync failed', failure.stage, failure.code, err);
    return emitStatus('error', failure);
  }
}

/**
 * Ask for notification permission NOW, synchronously inside the click: Safari
 * (and Firefox) only show the prompt from a user gesture, and an `await` before
 * the call (a config fetch) can use the gesture up. Old Safari takes a callback
 * and returns undefined, so both shapes are handled.
 */
function requestPermissionNow(): Promise<NotificationPermission> {
  return new Promise<NotificationPermission>((resolve) => {
    try {
      const ret = Notification.requestPermission((p) => resolve(p));
      if (ret && typeof (ret as Promise<NotificationPermission>).then === 'function') {
        (ret as Promise<NotificationPermission>).then(resolve, () => resolve(Notification.permission));
      }
    } catch {
      resolve(notificationPermission() as NotificationPermission);
    }
  });
}

/**
 * From a user gesture: ask for permission, then get a token and register it.
 *
 * When this transport already fetched a usable config (the banner / settings
 * status check did), the prompt is raised before any await, so it stays inside
 * the click. Without a known config it is fetched first (and nobody is asked
 * when push is off on the server).
 */
export async function enableWebPush(transport: WebPushTransport): Promise<WebPushStatus> {
  if (!isWebPushSupported()) return 'unsupported';
  const known = knownConfig.get(transport);
  if (known && !isUsableConfig(known)) return 'disabled';
  const asked = known && notificationPermission() === 'default' ? requestPermissionNow() : null;
  try {
    const config = known ?? (await step('config', () => fetchConfig(transport)));
    if (!isUsableConfig(config)) return 'disabled';
    let permission: NotificationPermission | 'unsupported';
    if (asked) permission = await asked;
    else {
      permission = notificationPermission();
      if (permission === 'default') permission = await requestPermissionNow();
    }
    if (permission === 'denied') {
      const failure = recordFailure(transport, {
        stage: 'permission',
        code: 'denied',
        message: 'Notification permission is denied for this site',
        kind: 'blocked',
      });
      return emitStatus('denied', failure);
    }
    if (permission !== 'granted') return 'default'; // prompt closed without a choice
    return emitStatus(await getTokenAndRegister(transport, config));
  } catch (err) {
    const failure = recordFailure(transport, classifyWebPushError(err, 'config'));
    console.warn('[webPush] enable failed', failure.stage, failure.code, err);
    return emitStatus('error', failure);
  }
}

/**
 * Sign-out / switch-off: deregister on the server, then delete the FCM token
 * (which also unsubscribes the browser). Best effort throughout: once the
 * subscription is gone FCM answers UNREGISTERED and the server retires the row.
 */
export async function disableWebPush(transport: WebPushTransport): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || isTauriApp()) return;
  try {
    const token = storedWebPushToken();
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (!token && !sub) return;
    if (token) {
      await transport.unregister(token).catch((err) => console.warn('[webPush] unregister failed', err));
      storeToken(null);
      try {
        const config = await transport.getConfig();
        const loaded =
          reg && isUsableConfig(config) && notificationPermission() === 'granted'
            ? await loadMessaging(config.firebase)
            : null;
        if (loaded && reg) {
          // Bind messaging to OUR worker first (getToken returns the cached
          // token, no new one): without it deleteToken registers Firebase's
          // default /firebase-messaging-sw.js, which this app does not serve.
          await loaded.mod.getToken(loaded.messaging, {
            vapidKey: config.vapid_key as string,
            serviceWorkerRegistration: reg,
          });
          await loaded.mod.deleteToken(loaded.messaging);
        }
      } catch (err) {
        console.warn('[webPush] deleteToken failed', err);
      }
    }
    // Whatever is left (deleteToken failed, or an old VAPID subscription).
    const left = reg ? await reg.pushManager.getSubscription() : null;
    if (left) await left.unsubscribe().catch(() => false);
  } catch (err) {
    console.warn('[webPush] disable failed', err);
  }
}

/** Current status without changing anything or loading Firebase (for settings screens). */
export async function getWebPushStatus(transport: WebPushTransport): Promise<WebPushStatus> {
  if (!isWebPushSupported()) return 'unsupported';
  try {
    const config = await fetchConfig(transport);
    if (!isUsableConfig(config)) return 'disabled';
    const permission = notificationPermission();
    if (permission === 'denied') return 'denied';
    if (permission !== 'granted') return 'default';
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return sub && storedWebPushToken() ? 'subscribed' : 'default';
  } catch {
    return 'error';
  }
}

// --------------------------------------------------------------------------- //
// transports
// --------------------------------------------------------------------------- //
const CONFIG_PATH = '/api/notifications/web-push/config';
const SUBSCRIBE_PATH = '/api/notifications/web-push/subscribe';
const CLIENT_ERROR_PATH = '/api/notifications/web-push/client-error';

/**
 * Staff (and anyone signed in through AuthContext). Pass `token` on sign-out:
 * the stored token may already be gone when the request leaves.
 */
export function staffTransport(token?: string | null): WebPushTransport {
  const opts = () =>
    (token ? { headers: { Authorization: `Bearer ${token}` }, skipAuthLogout: true } : {}) as Record<string, unknown>;
  const client = async () => (await import('@/services/apiClient')).apiClient;
  return {
    async getConfig() {
      const res = await (await client()).get<WebPushConfig>(CONFIG_PATH, opts());
      return res.data;
    },
    async register(body) {
      await (await client()).post(SUBSCRIBE_PATH, body, opts());
    },
    async unregister(token) {
      await (await client()).delete(SUBSCRIBE_PATH, { ...opts(), data: { fcm_token: token } });
    },
    async reportError(body) {
      // A rejected report must never sign anyone out.
      await (await client()).post(CLIENT_ERROR_PATH, body, { ...opts(), skipAuthLogout: true } as Record<
        string,
        unknown
      >);
    },
  };
}

/** Member portal (member_token). Pass `token` on sign-out, as above. */
export function memberTransport(token?: string | null): WebPushTransport {
  const call = async (path: string, init: RequestInit = {}) => {
    const { memberApiUrl, getMemberToken } = await import('@/lib/memberAuth');
    const bearer = token ?? getMemberToken();
    if (!bearer) throw new Error('Not authenticated');
    const res = await fetch(memberApiUrl(path), {
      ...init,
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearer}`,
        'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
      },
    });
    if (!res.ok) {
      throw Object.assign(new Error(`web push ${init.method || 'GET'} ${path}: ${res.status}`), { status: res.status });
    }
    return res;
  };
  return {
    async getConfig() {
      return (await (await call(CONFIG_PATH)).json()) as WebPushConfig;
    },
    async register(body) {
      await call(SUBSCRIBE_PATH, { method: 'POST', body: JSON.stringify(body) });
    },
    async unregister(token) {
      await call(SUBSCRIBE_PATH, { method: 'DELETE', body: JSON.stringify({ fcm_token: token }) });
    },
    async reportError(body) {
      await call(CLIENT_ERROR_PATH, { method: 'POST', body: JSON.stringify(body) });
    },
  };
}

// --------------------------------------------------------------------------- //
// "turn on notifications" banner: "Not now" lasts for this page load
// --------------------------------------------------------------------------- //
/**
 * The banner comes back on every full page load (refresh) and after every
 * sign-in until this browser is registered. "Not now" is therefore remembered
 * in memory only: a refresh starts a new page and shows it again, and sign-out
 * clears it (resetWebPushBannerDismissal) so the next sign-in in the same page
 * shows it too. (sessionStorage would outlive a refresh, which is exactly when
 * the banner should come back.)
 *
 * The first version hid the banner forever with a localStorage flag; that key
 * is removed so people who dismissed it then see it again.
 */
export const LEGACY_BANNER_DISMISSED_KEY = 'weavein:web-push-banner-dismissed';

let bannerDismissed = false;

export function isWebPushBannerDismissed(): boolean {
  return bannerDismissed;
}

export function dismissWebPushBanner(): void {
  bannerDismissed = true;
}

/** Sign-out / sign-in: the banner shows again for the next session. */
export function resetWebPushBannerDismissal(): void {
  bannerDismissed = false;
}

/** Forget the old permanent "dismissed" flag. Never throws. */
export function dropLegacyBannerDismissal(): void {
  try {
    localStorage.removeItem(LEGACY_BANNER_DISMISSED_KEY);
  } catch {
    /* storage blocked: nothing to drop */
  }
}
